import {
  dolomiteConfigs,
  dolomiteEmode,
  dolomiteIsolation,
} from '@1delta/data-sdk'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { computeUtilization, parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { buildDolomiteConfigs } from './emodeConfig'
import { resolveDolomiteRowIdentity } from './isolation'
import {
  getDolomiteMarketIds,
  DOLOMITE_MARKET_FNS,
  DOLOMITE_GLOBAL_FNS,
} from './publicCallBuild'

const BASE = 10n ** 18n
const SECONDS_PER_YEAR = 31536000

// per-market slice offsets (must match DOLOMITE_MARKET_FNS order)
const MARKET_STRIDE = DOLOMITE_MARKET_FNS.length // 7
const GLOBAL_COUNT = DOLOMITE_GLOBAL_FNS.length // 2
enum F {
  token = 0,
  isClosing = 1,
  totalPar = 2,
  currentIndex = 3,
  price = 4,
  marginPremium = 5,
  interestRate = 6,
}

const toBig = (v: any): bigint => {
  try {
    return BigInt(v ?? 0)
  } catch {
    return 0n
  }
}

// par (token-decimals) → wei (token-decimals): wei = par * index / 1e18
const parToWei = (par: any, index: any): bigint =>
  (toBig(par) * toBig(index)) / BASE

/**
 * Converter for Dolomite public market data. Emits one entry per asset under the
 * single `DOLOMITE` lender key (NOT per-market keys → not `isMultiMarket`).
 *
 * Data is assembled from per-field granular getters (the deployed contract lacks
 * `getMarketWithInfo`). Rates:
 *   borrowApr = ratePerSecond / 1e18 * SECONDS_PER_YEAR
 *   supplyApr = borrowApr * utilization * earningsRate   (no on-chain supply getter)
 *
 * Collateral factors are DERIVED — Dolomite has no per-asset LTV; it uses a
 * global `marginRatio` (R) modulated by a per-market `marginPremium` (p):
 *   collateralFactor       = 1 / (1 + p)               // liquidation-threshold collateral
 *   borrowCollateralFactor = 1 / ((1 + R) * (1 + p))   // safe max-LTV collateral
 *   borrowFactor           = 1 + p                       // borrow-side premium
 */
export const getDolomitePublicDataConverter = (
  lender: string,
  chainId: string,
  _prices: { [a: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [
  (data: any[]) => { data: Record<string, any>; chainId: string } | undefined,
  number,
] => {
  const marketIds = getDolomiteMarketIds(chainId)
  // Must mirror `buildDolomiteCall` exactly: a chain with the margin core
  // configured but no markets builds NO calls, so it declares none either.
  // Declaring the two global reads here while the builder emitted nothing is
  // what shifted every lender after DOLOMITE on BNB by two slots.
  const expectedNumberOfCalls =
    !dolomiteConfigs()?.[chainId]?.dolomiteMargin || marketIds.length === 0
      ? 0
      : GLOBAL_COUNT + MARKET_STRIDE * marketIds.length

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) return undefined

      const R = Number(parseRawAmount(toBig(data[0]?.value), 18)) // marginRatio
      const earningsRate = Number(parseRawAmount(toBig(data[1]?.value), 18)) // global earnings rate
      const emode = dolomiteEmode()?.[chainId]
      const isolation = dolomiteIsolation()?.[chainId]

      const result: Record<string, any> = {}

      marketIds.forEach((marketId, m) => {
        const base = GLOBAL_COUNT + MARKET_STRIDE * m
        const token = data[base + F.token]
        if (!token) return

        const underlying = String(token).toLowerCase()
        const meta = tokenList[underlying]
        const decimals = meta?.decimals ?? 18

        const totalPar = data[base + F.totalPar]
        const currentIndex = data[base + F.currentIndex]
        const totalDeposits = parseRawAmount(
          parToWei(totalPar?.supply, currentIndex?.supply),
          decimals,
        )
        const totalDebt = parseRawAmount(
          parToWei(totalPar?.borrow, currentIndex?.borrow),
          decimals,
        )

        // on-chain USD price: value = USD * 10^(36 - decimals)
        const price = Number(
          parseRawAmount(toBig(data[base + F.price]?.value), 36 - decimals),
        )

        // rates: per-second borrow → APR; supply derived from utilization & earnings
        const borrowRatePerSec = Number(
          parseRawAmount(toBig(data[base + F.interestRate]?.value), 18),
        )
        const variableBorrowRate = borrowRatePerSec * SECONDS_PER_YEAR * 100
        const util =
          Number(totalDeposits) > 0
            ? Number(totalDebt) / Number(totalDeposits)
            : 0
        const depositRate = variableBorrowRate * util * earningsRate

        // §3 collateral-factor derivation — base mode `'0'` + per-category
        // e-mode configs (auto-selected per position in user-data).
        const p = Number(
          parseRawAmount(toBig(data[base + F.marginPremium]?.value), 18),
        )
        const isClosing = Boolean(data[base + F.isClosing])
        const config = buildDolomiteConfigs(
          marketId,
          p,
          R,
          isClosing,
          emode,
          isolation,
        )

        const liquidity = Number(totalDeposits) - Number(totalDebt)
        // Dolomite's protocol-native key is the integer marketId, not the token
        // address (a token can be relisted under a new marketId). Key by marketId
        // so the uid is collision-proof and matches the calldata-side identity.
        const marketUid = createMarketUid(chainId, lender, String(marketId))

        const identity = resolveDolomiteRowIdentity(
          chainId,
          lender,
          marketId,
          underlying,
          tokenList,
        )
        result[marketUid] = {
          marketUid,
          name: identity.name,
          poolId: underlying,
          underlying,
          asset: identity.asset,
          marketId,
          totalDeposits,
          totalDebt,
          totalDepositsUSD: price * Number(totalDeposits),
          totalDebtUSD: price * Number(totalDebt),
          totalLiquidity: liquidity,
          borrowLiquidity: liquidity,
          totalLiquidityUSD: liquidity * price,
          borrowLiquidityUSD: liquidity * price,
          utilization: computeUtilization(totalDeposits, totalDebt),
          price,
          depositRate,
          variableBorrowRate,
          stableBorrowRate: 0,
          collateralActive: true,
          // By ASSET GROUP (the map's key), the underlying's for an isolation
          // market — see `isolation.ts`.
          intrinsicYield:
            additionalYields?.intrinsicYields?.[identity.yieldKey] ?? 0,
          rewards: [],
          config,
          closeFactor: 1,
          borrowingEnabled: !isClosing,
          // `isClosing` gates BORROWS ONLY — DolomiteMargin's own comment on the
          // field is "whether additional borrows are allowed", and
          // `_verifyFinalState` only asserts the market's total borrow par did
          // not grow. Supplying is untouched, which is the whole point of the
          // flag: Dolomite marks yield-bearing assets (weETH, sUSDe, wstETH, the
          // dGM/dPT wrappers) borrow-disabled precisely so they can be SUPPLIED
          // as collateral. Mapping it onto deposits made 60/77 Arbitrum, 39/48
          // Berachain and 14/23 Ethereum markets report "deposits disabled" —
          // and the term sheet then printed a red "Currently unavailable" over
          // the collateral leg of a loop that builds and executes fine. The
          // borrow half is already carried correctly by `config[*].debtDisabled`.
          depositsEnabled: true,
          ...(identity.isolation && {
            isolation: identity.isolation,
            allowedDebtMarketUids: identity.isolation.allowedDebtMarketUids,
          }),
        }
      })

      return { data: result, chainId }
    },
    expectedNumberOfCalls,
  ]
}
